import { useState, useMemo, useCallback, useEffect } from "react";
import { MagnifyingGlassIcon, XMarkIcon, ArrowTopRightOnSquareIcon, ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";
import AccordionSection from "./AccordionSection";
import FilterMenu from "../filter-menu";

export interface FilterPanelProps {
  /** Array of filter menu configurations */
  filterMenus: FilterMenuConfig[];
  /** Current filter selections by menu ID */
  selections: Record<string, string[]>;
  /** Callback when selections change */
  onChange: (selections: Record<string, string[]>) => void;
  /** Whether the panel is open/visible */
  isOpen: boolean;
  /** Optional key for persisting expansion state */
  persistKey?: string;
  /** @deprecated Use onClearSelections instead */
  footer?: React.ReactNode;
  /** Callback to close the panel */
  onClose?: () => void;
  /** Callback to clear all selections */
  onClearSelections?: () => void;
  /** Panel variant for visual distinction */
  variant?: "filter" | "highlight";
}

export default function FilterPanel({
  filterMenus,
  selections,
  onChange,
  isOpen,
  persistKey,
  footer,
  onClose,
  onClearSelections,
  variant,
}: FilterPanelProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Persist which sections are expanded
  const [expandedSections, setExpandedSections] = useStateWithOptionalPersistence<string[]>(
    persistKey ? `${persistKey}-expanded` : undefined,
    []
  );

  // Calculate selection counts per menu
  const selectionCounts = useMemo(() => {
    return filterMenus.reduce((acc, menu) => {
      acc[menu.id] = selections[menu.id]?.length || 0;
      return acc;
    }, {} as Record<string, number>);
  }, [filterMenus, selections]);

  // Total selections for header badge
  const totalSelections = useMemo(() => {
    return Object.values(selectionCounts).reduce((sum, count) => sum + count, 0);
  }, [selectionCounts]);

  // Build flat list of all active selections with labels, grouped by menu
  const activeItems = useMemo(() => {
    const groups: { menuId: string; menuLabel: string; items: { id: string; label: string }[] }[] = [];

    for (const menu of filterMenus) {
      const selectedIds = selections[menu.id] || [];
      if (selectedIds.length === 0) continue;

      const items: { id: string; label: string }[] = [];
      for (const id of selectedIds) {
        const option = menu.filterOptions.find((o) => String(o.id) === String(id));
        if (option && !option.isHeader) {
          items.push({ id: String(option.id), label: option.label });
        }
      }
      if (items.length > 0) {
        // Clean up menu label
        let menuLabel = menu.label;
        if (menuLabel.startsWith("Filter by ")) menuLabel = menuLabel.replace("Filter by ", "");
        if (menuLabel.startsWith("Highlight by ")) menuLabel = menuLabel.replace("Highlight by ", "");
        groups.push({ menuId: menu.id, menuLabel, items });
      }
    }
    return groups;
  }, [filterMenus, selections]);

  const [activeExpanded, setActiveExpanded] = useState(false);

  // Toggle section expansion (single-expand mode: only one section open at a time)
  const toggleSection = useCallback((menuId: string) => {
    setExpandedSections((prev) => {
      if (prev.includes(menuId)) {
        // Collapse if already expanded
        return prev.filter((id) => id !== menuId);
      }
      // Single-expand: replace with just this section (auto-collapses others)
      return [menuId];
    });
  }, [setExpandedSections]);

  // Calculate disabled states based on mutual exclusivity rules
  const processedMenus = useMemo(() => {
    const hasCustomFiltersSelected = (selections["category-filter"]?.length || 0) > 0;
    const hasBasicFiltersSelected = (selections["basic-filter"]?.length || 0) > 0;
    const hasPortfolioFiltersSelected =
      (selections["portfolios-filter"]?.length || 0) > 0 ||
      (selections["portfolios-filter-prebuilt"]?.length || 0) > 0;
    const hasNonCustomFiltersSelected = hasBasicFiltersSelected || hasPortfolioFiltersSelected;

    return filterMenus.map((menu) => {
      if (menu.id === "portfolios-filter" || menu.id === "portfolios-filter-prebuilt" || menu.id === "basic-filter") {
        const disabledHelperText = hasCustomFiltersSelected
          ? "Disabled - clear advanced filters"
          : menu.helperText;

        return {
          ...menu,
          disabled: hasCustomFiltersSelected,
          helperText: disabledHelperText,
        };
      } else if (menu.id === "category-filter") {
        const disabledHelperText = hasNonCustomFiltersSelected
          ? "Disabled - clear basic filters"
          : menu.helperText;

        return {
          ...menu,
          disabled: hasNonCustomFiltersSelected,
          helperText: disabledHelperText,
          filterOptions: menu.filterOptions.map((option) => ({
            ...option,
            disabled: hasCustomFiltersSelected
              ? !selections["category-filter"]?.includes(String(option.id))
              : false,
          })),
        };
      }
      return menu;
    });
  }, [filterMenus, selections]);

  // Filter menus based on search query
  const filteredMenus = useMemo(() => {
    if (!searchQuery.trim()) {
      return processedMenus;
    }

    const query = searchQuery.toLowerCase();
    return processedMenus
      .map((menu) => {
        // Filter options by search query
        const filteredOptions = menu.filterOptions.filter((option) =>
          option.label.toLowerCase().includes(query)
        );

        // Also filter subsections if they exist
        const filteredSubsections = menu.subsections?.map((sub) => ({
          ...sub,
          filterIds: sub.filterIds.filter((id) => {
            const option = menu.filterOptions.find((opt) => opt.id === id);
            return option?.label.toLowerCase().includes(query);
          }),
        })).filter((sub) => sub.filterIds.length > 0);

        return {
          ...menu,
          filterOptions: filteredOptions,
          subsections: filteredSubsections?.length ? filteredSubsections : undefined,
        };
      })
      .filter((menu) => menu.filterOptions.length > 0);
  }, [processedMenus, searchQuery]);

  // Handle toggle with disabled check
  const handleToggle = useCallback(
    (menuId: string, filterId: string, isDisabled: boolean) => {
      if (isDisabled) return;

      const currentSelections = selections[menuId] || [];
      const isSelected = currentSelections.includes(filterId);

      const updatedSelections = isSelected
        ? currentSelections.filter((id) => id !== filterId)
        : [...currentSelections, filterId];

      onChange({
        ...selections,
        [menuId]: updatedSelections,
      });
    },
    [selections, onChange]
  );

  // Clear search when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Auto-expand sections with matching search results
  useEffect(() => {
    if (searchQuery.trim() && filteredMenus.length > 0) {
      const menusWithResults = filteredMenus.map((m) => m.id);
      setExpandedSections((prev) => {
        const newExpanded = [...new Set([...prev, ...menusWithResults])];
        return newExpanded;
      });
    }
  }, [searchQuery, filteredMenus, setExpandedSections]);

  if (!isOpen) return null;

  return (
    <div
      className={clsx(
        "bg-gray-800 shadow-lg shadow-black/30",
        variant === "highlight" && "border-x border-b border-gray-700/50",
        variant === "filter" && "border-x border-b border-squawk-blue/50",
        !variant && "border-b border-gray-700/50"
      )}
    >
      {/* Header with search, clear, and close */}
      <div className="px-3 py-2 border-b border-gray-700/30">
        <div className="flex items-center gap-2">
          {/* Search input */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search watchlists, filters..."
              className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="size-3" />
              </button>
            )}
          </div>

          {/* Clear selections button */}
          {onClearSelections && totalSelections > 0 && (
            <button
              onClick={onClearSelections}
              className="px-2 py-1 text-xs font-medium rounded border border-red-700/50 text-red-400 hover:border-red-600 hover:bg-red-900/20 transition-colors whitespace-nowrap"
            >
              Clear
            </button>
          )}

          {/* Close panel button */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="size-4" />
            </button>
          )}
        </div>
      </div>

      {/* Active selections summary */}
      {totalSelections > 0 && (
        <div className="border-b border-gray-700/30">
          <button
            onClick={() => setActiveExpanded(!activeExpanded)}
            className={clsx(
              "w-full px-3 py-2 flex items-center gap-2 text-left text-xs transition-colors focus:outline-none",
              activeExpanded
                ? variant === "highlight" ? "bg-terminal-orange/5 text-terminal-orange" : "bg-squawk-blue/5 text-squawk-blue"
                : "text-gray-400 hover:text-gray-200"
            )}
          >
            {activeExpanded
              ? <ChevronDownIcon className="size-3 flex-shrink-0" />
              : <ChevronRightIcon className="size-3 flex-shrink-0" />
            }
            <span className="font-medium flex-1">Active</span>
            <span className={clsx(
              "px-1.5 py-0.5 text-2xs font-medium rounded",
              variant === "highlight" ? "bg-terminal-orange/20 text-terminal-orange" : "bg-squawk-blue/20 text-squawk-blue"
            )}>
              {totalSelections}
            </span>
          </button>
          {activeExpanded && (
            <div className="px-3 pb-2 space-y-2">
              {activeItems.map((group) => (
                <div key={group.menuId}>
                  <div className="text-2xs text-gray-500 uppercase tracking-wider mb-1">{group.menuLabel}</div>
                  <div className="flex flex-wrap gap-1">
                    {group.items.map((item) => (
                      <span
                        key={item.id}
                        className={clsx(
                          "inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded text-xs border",
                          variant === "highlight"
                            ? "bg-terminal-orange/10 border-terminal-orange/30 text-terminal-orange"
                            : "bg-squawk-blue/10 border-squawk-blue/30 text-squawk-blue"
                        )}
                      >
                        {item.label}
                        <button
                          onClick={() => handleToggle(group.menuId, item.id, false)}
                          className="p-0.5 hover:text-gray-200 transition-colors"
                        >
                          <XMarkIcon className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Accordion sections */}
      <div className="divide-y divide-gray-700/30 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {filteredMenus.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-gray-500">
            No filters match &quot;{searchQuery}&quot;
          </div>
        ) : (
          filteredMenus.map((menu) => (
            <AccordionSection
              key={menu.id}
              label={menu.label}
              activeCount={selectionCounts[menu.id] || 0}
              isExpanded={expandedSections.includes(menu.id)}
              onToggle={() => toggleSection(menu.id)}
              disabled={menu.disabled}
              helperText={menu.helperText}
            >
              <FilterMenu
                label={menu.label}
                description={menu.description}
                filters={menu.filterOptions}
                selected={selections[menu.id] || []}
                onToggle={(filterId) =>
                  handleToggle(menu.id, String(filterId), menu.disabled ?? false)
                }
                disabled={menu.disabled}
                subsections={menu.subsections}
                footer={menu.footer}
                emptyLabel="No options available"
                className="!static !min-w-0 !max-w-none !w-full !rounded-none !p-0 !space-y-1.5"
                showCount={false}
                collapsible={false}
                hideHeader
              />
            </AccordionSection>
          ))
        )}
      </div>

      {/* Manage in Library link */}
      <div className="px-3 py-2 border-t border-gray-700/30">
        <a
          href="/watchlists-filters"
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-squawk-blue transition-colors"
        >
          <ArrowTopRightOnSquareIcon className="size-3.5" />
          Manage in Library
        </a>
      </div>
    </div>
  );
}
